import React from 'react';
import {Platform, View} from 'react-native';
import {IStateProps} from './model';
import {useStyles} from '../../../../../shared/hooks/styles';
import {ApplicationsItemStatus} from '../itemStatus';
import {stylePatterns} from '../../../../../shared/theme/patterns';
import {useIsDesktop} from '../../../../../shared/hooks/useIsDesktop';
import {Text} from '../../../../../shared/components/text';
import {LoadableContent} from '../../../../../shared/components/loadableContent';
import {DeviceSize} from '../../../../../shared/theme/helpers';
import {useMap} from '../../../../login/esiaRegCard/components/map/useMap';
import {images} from '../../../../../assets/icons';
import {Link} from '../../../../../shared/components/link';
import {formatPhone, formatPhoneToCallable} from '../../../../../shared/pipes';
import {useTheme} from '../../../../../shared/theme';
import {DeliveryType} from '../../../../../types/registerCardApplicationRequestPayload';
import {CardApplicationStatus} from '../../../../../types/enums/cardApplicationStatus';
import {CardStatus} from '../../../../../types/enums/cardStatus';
import {ExternalLink} from '../../../../../shared/components/externalLink';
import {GetIssuePointCabinetResponsePayload} from '../../../../../shared/models/api/getIssuePointCabinetResponsePayload';

type CardApplicationStatusDescribed = Record<keyof typeof CardApplicationStatus, string> & {[key: string]: string};
type CardStatusDescribed = Record<keyof typeof CardStatus, string> & {[key: string]: string};

const _cardApplicationsStatuses: CardApplicationStatusDescribed = {
  AcceptFromClient: 'Принято',
  OnReview: 'На рассмотрении',
  Accepted: 'Одобрено',
  Declined: 'Отказано',
};

const _cardStatuses: CardStatusDescribed = {
  New: 'Выпущена',
  Active: 'Активная',
  Blocked: 'Заблокирована',
  Returned: 'Возвращена клиентом',
  InDelivery: 'В доставке',
  Arrived: 'Прибыло в место вручения',
  Reserved: 'Зарезервирована',
};

const russianPostTrackingUrl = 'https://www.pochta.ru/tracking?barcode=';

export default ({subjects, historyTable, statusId, statusName, application}: IStateProps) => {
  const isDesktop = useIsDesktop();

  const styles = useStyles(({theme, mediaOnly, addShadow}) => ({
    desktopTitle: {
      ...theme.fonts.h6,
      alignSelf: 'flex-start',
    },
    statusWrap: {
      display: 'flex',
      flexDirection: 'row',
      ...mediaOnly(
        DeviceSize.desktop,
        {justifyContent: 'space-between', alignItems: 'center'},
        {justifyContent: 'flex-end'},
      ),
    },
    statusDeliveryWrap: {
      display: 'flex',
      marginBottom: isDesktop ? 10 : 0,
      flexDirection: 'column',
      ...(isDesktop ? theme.fonts.h6 : {}),
      ...mediaOnly(
        DeviceSize.desktop,
        {justifyContent: 'space-between', alignItems: 'center'},
        {justifyContent: 'flex-end'},
      ),
    },
    subjectWrap: {
      paddingTop: isDesktop ? 30 : 20,
    },
    subjectItem: {
      flexDirection: 'row',
      justifyContent: 'space-between',
      alignItems: 'flex-start',
      paddingBottom: 20,
    },
    subjectItemLabel: {
      ...mediaOnly(
        DeviceSize.desktop,
        {...theme.fonts.body, minWidth: 120},
        {...theme.fonts.bodySmallLight, minWidth: 100},
      ),
      color: theme.applications.labelColor,
    },
    subjectItemValue: {
      ...mediaOnly(
        DeviceSize.desktop,
        {...theme.fonts.h5Light, paddingLeft: 24, textAlign: 'left'},
        {...theme.fonts.body, paddingLeft: 10, textAlign: 'right'},
      ),
      flexShrink: 1,
      flexGrow: 1,
      overflow: 'hidden',
    },
    historyWrap: isDesktop
      ? {
          padding: 0,
          margin: 0,
        }
      : {
          marginTop: 15,
        },
    historyTitle: theme.fonts.h6,
    historyTableWrap: {
      paddingTop: isDesktop ? 16 : 13,
    },
    historyTableHeader: {
      display: 'flex',
      flexDirection: 'row',
      paddingBottom: 4,
    },
    historyTableHeaderItem: {
      ...mediaOnly(DeviceSize.desktop, theme.fonts.body, theme.fonts.bodySmallLight),
      flex: 1,
      color: theme.applications.textCaptionColor,
    },
    historyTableContent: {
      ...stylePatterns.flexCenterVertical,
      minHeight: 50,
      borderColor: theme.applications.tableRowSeparatorColor,
      borderTopWidth: 1,
      display: 'flex',
      flexDirection: 'row',
    },
    historyTableContentItem: {
      ...mediaOnly(DeviceSize.desktop, theme.fonts.h6Light, theme.fonts.bodySmallLight),
      paddingRight: 10,
    },
    historyTableRow: isDesktop
      ? {}
      : {
          paddingLeft: 4,
          marginLeft: -4,
        },
    form: {
      alignItems: 'center',
      width: '100%',
    },
    formSubmitBox: {
      justifyContent: 'center',
      alignItems: 'center',
      marginBottom: 20,
      marginTop: 0,
      width: '100%',
    },
    formSubmitBtnContainer: {
      paddingHorizontal: 20,
      width: '100%',
      justifyContent: 'center',
      alignItems: 'center',
    },
    submit: {
      marginTop: 14,
      width: isDesktop ? '30%' : '90%',
    },
    checkbox: {
      flexDirection: 'row',
      alignItems: 'flex-start',
      justifyContent: 'center',
      ...mediaOnly(DeviceSize.desktop, {width: '100%'}),
      ...mediaOnly(DeviceSize.tablet, {width: 560}),
      ...mediaOnly(DeviceSize.mobile, {width: 260}),
    },
    checkboxBtn: {
      marginRight: 12,
    },
    agreement: {
      ...mediaOnly(DeviceSize.desktop, {}, {flex: 1}),
      flexDirection: 'row',
      alignItems: 'center',
      flexWrap: 'wrap',
    },
    agreementText: {
      ...mediaOnly(DeviceSize.desktop, theme.fonts.body, {...theme.fonts.bodySmallLight, lineHeight: 24}),
    },
    agreementTextDoc: {
      color: theme.color.primaryColor,
    },
    deliveryInfoContainer: {
      width: '100%',
      marginTop: isDesktop ? 0 : 5,
      paddingHorizontal: isDesktop ? 14 : 0,
      position: 'relative',
      ...(isDesktop ? {alignItems: 'flex-start'} : {}),
    },
    subtitle: {
      textAlign: isDesktop ? 'center' : 'left',
      marginTop: 10,
      marginBottom: 10,
      position: 'relative',
    },
    table: {
      // TODO: put it back when the map works
      padding: 24,
      paddingTop: 24,
      paddingBottom: 15,
      maxHeight: 347,
      flexDirection: 'row',
      backgroundColor: theme.color.backgroundMenuColor,
    },
    showTable: {
      transform: [{translateY: 0}],
    },
    hideTable: {
      transform: [{translateY: -250}],
    },
    mapBlock: {
      ...mediaOnly(DeviceSize.desktop, {marginTop: 24}),
      width: '100%',
      borderRadius: 5,
      ...addShadow({height: 2, radius: 18, opacity: 0.1}),
    },
    mapWrapper: {
      height: 480,
    },
    column: {
      minWidth: 70,
      flex: 1,
    },
    firstColumn: {
      marginRight: 20,
    },
    row: {
      marginBottom: isDesktop ? 20 : 0,
      marginTop: isDesktop ? 0 : 15,
      fontWeight: isDesktop ? 'normal' : 'bold',
    },
    rowWithIcon: {
      flexDirection: 'row',
    },
    icon: {
      marginRight: 12,
      color: theme.color.primaryColor,
      justifyContent: 'center',
      height: 30,
      marginTop: isDesktop ? 0 : 15,
    },
    map: {
      width: '100%',
      height: '100%',
    },
    groupItem: {
      flexDirection: 'row',
      alignItems: 'flex-start',
      marginBottom: 12,
      paddingVertical: 5,
    },
    cluster: {
      color: theme.color.primaryColor,
    },
    clusterText: {
      color: theme.color.labelText,
    },
    issuePointInfoContainer: {
      flexDirection: 'column',
      alignItems: 'flex-start',
      justifyContent: 'flex-start',
      width: '100%',
      paddingHorizontal: 16,
    },
    issuePointInfoText: {
      flexShrink: isDesktop ? 1 : 0,
      marginTop: isDesktop ? 0 : 15,
    },
    trackingNumberLink: {
      color: theme.color.linkColor,
    },
  }));

  const theme = useTheme();

  const {mapDataRaw} = useMap();

  const selectedIssuePoint = React.useMemo<GetIssuePointCabinetResponsePayload>(
    () => mapDataRaw.find((x) => x.id === application?.cardDeliveryInfo?.counterpartyDepartmentId)!,
    [mapDataRaw, application?.cardDeliveryInfo?.counterpartyDepartmentId],
  );

  return (
    <View>
      {isDesktop && (
        <View style={styles.statusWrap}>
          <Text style={styles.desktopTitle}>Заявление</Text>
          <ApplicationsItemStatus statusId={statusId} statusName={statusName} />
        </View>
      )}
      <LoadableContent loading={!application}>
        {!isDesktop && (
          <View style={styles.statusWrap}>
            <ApplicationsItemStatus statusId={statusId} statusName={statusName} />
          </View>
        )}
        <View style={styles.subjectWrap} testID='application-subjects'>
          {subjects.map(({label, value}, i) => (
            <View style={styles.subjectItem} key={i} testID={`application-subject-${i}`}>
              <Text style={styles.subjectItemLabel} testID={`application-subject-${i}-label`}>
                {label}:
              </Text>
              <Text style={styles.subjectItemValue} testID={`application-subject-${i}-value`}>
                {value}
              </Text>
            </View>
          ))}
        </View>
        {application?.statusId === 3 && application.cardDeliveryInfo?.deliveryTypeId === 1 ? null : (
          <View style={styles.statusDeliveryWrap}>
            <Text style={styles.desktopTitle}>Информация по доставке</Text>
            <View style={styles.form}>
              {application?.cardDeliveryInfo?.deliveryTypeId === DeliveryType.RussianPost ? (
                <View style={styles.deliveryInfoContainer}>
                  <Text type={isDesktop ? 'h6Light' : 'body'} style={styles.subtitle}>
                    Адрес получателя: {application.cardDeliveryInfo.desiredAddress}
                  </Text>
                  {application.cardDeliveryInfo?.trackingNumber != null && (
                    <Text type={isDesktop ? 'h6Light' : 'body'} style={styles.subtitle}>
                      Трек-номер (Почта России):{' '}
                      {Platform.OS === 'web' ? (
                        <a href={russianPostTrackingUrl + application.cardDeliveryInfo?.trackingNumber} target='_blank'>
                          {application.cardDeliveryInfo?.trackingNumber}
                        </a>
                      ) : (
                        <ExternalLink
                          testID='trackingNumber'
                          style={styles.trackingNumberLink}
                          link={russianPostTrackingUrl + application.cardDeliveryInfo?.trackingNumber}
                          text={application.cardDeliveryInfo?.trackingNumber}
                        />
                      )}
                    </Text>
                  )}
                </View>
              ) : (
                <>
                  {Platform.OS !== 'web' && selectedIssuePoint != null && (
                    <View style={styles.issuePointInfoContainer}>
                      <Text style={styles.row} type={isDesktop ? 'h6Light' : 'body'}>
                        Отделение {selectedIssuePoint.name ?? selectedIssuePoint.organization?.name}
                      </Text>
                      <View style={[styles.row, styles.rowWithIcon]}>
                        <View style={styles.icon}>
                          <images.Location width={24} height={24} color={theme.color.primaryColor} />
                        </View>
                        <Text style={styles.issuePointInfoText} type={isDesktop ? 'h6Light' : 'body'}>
                          {selectedIssuePoint.address}
                        </Text>
                      </View>
                      <View style={styles.rowWithIcon}>
                        <Link
                          testID='phone-geo-map-link-id'
                          href={`tel:${formatPhoneToCallable(selectedIssuePoint.phone)}`}
                          external
                          style={styles.groupItem}>
                          <View style={styles.icon}>
                            <images.Phone width={24} height={24} color={theme.color.primaryColor} />
                          </View>
                          <Text style={styles.issuePointInfoText} type={isDesktop ? 'h6Light' : 'body'}>
                            {formatPhone(selectedIssuePoint.phone)}
                          </Text>
                        </Link>
                      </View>
                      <Text style={[styles.row, {marginTop: 0}]} type={isDesktop ? 'h6' : 'body'}>
                        Часы работы
                      </Text>
                      <View style={styles.rowWithIcon}>
                        <View style={styles.icon}>
                          <images.Clock2 width={24} height={24} color={theme.color.primaryColor} />
                        </View>
                        <Text style={styles.issuePointInfoText} type={isDesktop ? 'h6' : 'body'}>
                          {selectedIssuePoint.operatingMode}
                        </Text>
                      </View>
                    </View>
                  )}
                </>
              )}
              {application?.cardDeliveryInfo?.deliveryTypeId === DeliveryType.IssuePoint &&
                selectedIssuePoint &&
                Platform.OS === 'web' && (
                  <View style={styles.form}>
                    <View style={styles.table}>
                      <View style={[styles.column, styles.firstColumn]}>
                        <Text style={styles.row} type='h6'>
                          Отделение {selectedIssuePoint.name}
                        </Text>
                        <View style={[styles.row, styles.rowWithIcon]}>
                          <View style={styles.icon}>
                            <images.Location width={24} height={24} color={theme.color.primaryColor} />
                          </View>
                          <Text type='h6Light'>{selectedIssuePoint?.address}</Text>
                        </View>
                        <View style={styles.rowWithIcon}>
                          <View style={styles.icon}>
                            <images.Phone width={24} height={24} color={theme.color.primaryColor} />
                          </View>
                          <Text type='h6Light'>{selectedIssuePoint?.phone}</Text>
                        </View>
                      </View>
                      <View style={styles.column}>
                        <Text style={styles.row} type='h6'>
                          Часы работы
                        </Text>
                        <View style={styles.rowWithIcon}>
                          <View style={styles.icon}>
                            <images.Clock2 width={24} height={24} color={theme.color.primaryColor} />
                          </View>
                          <Text type='h6Light'>{selectedIssuePoint?.operatingMode}</Text>
                        </View>
                      </View>
                    </View>
                  </View>
                )}
            </View>
          </View>
        )}
        <View style={styles.historyWrap} testID='application-history'>
          <Text style={styles.historyTitle} testID='application-history-title'>
            История рассмотрения
          </Text>
          <View style={styles.historyTableWrap}>
            {historyTable.map((row, rowIndex) => (
              <View
                style={[
                  rowIndex === 0 ? styles.historyTableHeader : styles.historyTableContent,
                  styles.historyTableRow,
                ]}
                key={rowIndex}
                testID={`application-history-item-${rowIndex}`}>
                {row.map((value, valueIndex) => {
                  return (
                    <Text
                      style={[
                        rowIndex === 0 ? styles.historyTableHeaderItem : styles.historyTableContentItem,
                        rowIndex === 0 && valueIndex === 1
                          ? {...styles.historyTableHeaderItem, marginRight: 55}
                          : styles.historyTableContentItem,
                        valueIndex % 2 === 1 ? {marginLeft: 50} : {},
                      ]}
                      key={valueIndex}
                      testID={`application-history-item-${rowIndex}-body-${valueIndex}`}>
                      {value}
                    </Text>
                  );
                })}
              </View>
            ))}
          </View>
        </View>
      </LoadableContent>
    </View>
  );
};
